import { useState, useEffect } from "react";
import {
  getAuth,
  onAuthStateChanged,
  signInWithRedirect,
  signOut,
  User,
  signInWithEmailAndPassword,
  signInWithPopup,
  OAuthProvider,
} from "firebase/auth";
import { app } from "@/app/firebase/config";
import { env } from "@/app/env";
import { UserIsAdmin } from "@/app/api/Access";

const auth = getAuth(app);

const microsoftProvider = new OAuthProvider(
  "microsoft.com",
).setCustomParameters({
  tenant: env.NEXT_PUBLIC_MICROSOFT_OAUTH_TENANT_ID,
});

const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [userIsSuperAdmin, setUserIsSuperAdmin] = useState<boolean>(false);
  const [userIsOrgAdmin, setUserIsOrgAdmin] = useState<boolean>(false);
  const [adminOrgCodes, setAdminOrgCodes] = useState<string[]>([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      UserIsAdmin(user!).then((response) => {
        setUserIsSuperAdmin(response.superAdmin);
        setAdminOrgCodes(response.orgRoles);
        setUserIsOrgAdmin(response.orgRoles.length > 0);
      });
    });

    return () => unsubscribe(); // Clean up the listener on unmount
  }, []);

  return { user, loading, userIsSuperAdmin, userIsOrgAdmin, adminOrgCodes };
};

const handleLogin = async () => {
  if (window.location.hostname === "localhost") {
    if (
      process.env.NEXT_PUBLIC_E2E_TEST_AUTH_EMAIL &&
      process.env.NEXT_PUBLIC_E2E_TEST_AUTH_PASSWORD &&
      document.cookie.split("; ").includes("__e2e_test__=true")
    ) {
      // We don't want the API gateway to allow tokens from the auth emulator,
      // but we don't want to give the E2E tests someone's real credentials
      // (and can't add test users in Entra ID because it only has one
      // environment - prod), so we've added an email/password user in the
      // Firebase project for the E2E tests to use: "u1111111@anu.edu.au"
      await signInWithEmailAndPassword(
        auth,
        process.env.NEXT_PUBLIC_E2E_TEST_AUTH_EMAIL,
        process.env.NEXT_PUBLIC_E2E_TEST_AUTH_PASSWORD,
      );
    } else {
      // The redirect method fails when running locally because of third-party
      // cookies/storage issues. See
      // <https://firebase.google.com/docs/auth/web/redirect-best-practices>.
      await signInWithPopup(auth, microsoftProvider);
    }
  } else {
    await signInWithRedirect(auth, microsoftProvider);
  }
};

const handleLogout = async () => {
  await signOut(auth);
};

export { auth, handleLogin, handleLogout, useAuth };
